import React, { useCallback, useMemo } from "react";
import { Details } from "components/src/components/Details/Details";
import { DetailBgColorType, DetailType } from "components/src/components/Details/types";
import { MarketTemplateEventHeaderProps } from "../../types";
import { EventDetails } from "../../../EventDetails/EventDetails";
import { normalizeSport } from "shared/src/utils/sportMap";
import { Competitor } from "../../../Competitor/Competitor";
import { CompetitorType } from "../../../Competitor/types";
import { Icon } from "components/src/components/Icon/Icon";
import { mapperFactory as eventDetailsToCompetitorSyncMapper } from "components-sbk/src/components/MarketTemplate/js/prepareMarkets/mappers/mapCompetitorSync";
import { SyncType } from "shared/src/hooks/useSync";
import cx from "components/src/utils/cx";

type EventHeaderCompetitors = {
  firstCompetitor?: CompetitorType;
  secondCompetitor?: CompetitorType;
  isLive?: boolean;
  showChevron?: boolean;
  hasEventDetails?: boolean;
  hasSync?: boolean;
};

export const EventHeader = ({
  onEventHeaderClick,
  id,
  sport,
  isOddsBoost,
  eventDetails,
  showTeamsInEventHeader,
  marginClasses = "cui-mb-4",
  paddingClasses = "",
  className = "",
  mode = "light",
  showChevron = true,
  swapCompetitors = false,
  competitionType = "team",
  boostLogoType = "compact",
  ...rest
}: MarketTemplateEventHeaderProps) => {
  const hasOnClickHandler = typeof onEventHeaderClick === "function";
  const isFullBoost = boostLogoType === "full";
  // const _eventName: DetailType[] = [
  //   isOddsBoost
  //     ? { leftIcon: { name: isFullBoost ? "brand_boost_logo" : "icon_ui_boost", size: isFullBoost ? "3xl" : "sm", color: "fg-global-black" }, noMargin: true, classes: "cui-py-1 cui-px-2 cui-mr-2px", bgColor: "highlight-five" as DetailBgColorType }
  //     : null,
  //   {
  //     tag: eventDetails?.eventName,
  //     textSize: isOddsBoost ? "display-sm-bold" : "heading-sm-bold",
  //     fgColor: "fg-default",
  //     showDivider: !isOddsBoost,
  //     allowTextWrap: true,
  //   },
  //   (showTeamsInEventHeader || !hasOnClickHandler || isOddsBoost)
  //     ? null
  //     : {
  //         leftIcon: {
  //           name: "ui_chevron_right",
  //           size: 14,
  //           color: "fg-primary",
  //         },
  //         showDivider: false,
  //       },
  // ];

  const _onEventHeaderClick = useCallback(
    (e) => {
      if (onEventHeaderClick && typeof onEventHeaderClick === "function")
        onEventHeaderClick(e, id);
    },
    [onEventHeaderClick]
  );

  const { firstCompetitor, secondCompetitor, hasCompetitors, hasSync } = useMemo(() => {
    const isSoccer = (normalizeSport(sport)) === "football";
    const hasSync = eventDetails && "sync" in eventDetails;
    const hasCompetitors = hasSync || Boolean(
      (eventDetails?.home && eventDetails?.away) ||
        (eventDetails?.competitor1 && eventDetails?.competitor2)
    );

    const competitorsFromEventDetails = [
      eventDetails?.away ?? eventDetails?.competitor2,
      eventDetails?.home ?? eventDetails?.competitor1,
    ].map((competitor, i) => {
      if (!competitor && !hasSync) return competitor;
      const _sport = eventDetails?.sport ?? sport;
      const isIndividual = competitionType === "individual" || _sport == "tennis";
      let syncMapKey;
      if (i === 0) {
        syncMapKey = isIndividual ? "competitor2" : "away";
      } else {
        syncMapKey = isIndividual ? "competitor1" : "home";
      }
      let syncMapped;
      if (hasSync) {
        syncMapped = {
          subscribe: eventDetails?.sync?.subscribe,
          getSnapshot: eventDetails?.sync?.getSnapshot,
          mapper: eventDetailsToCompetitorSyncMapper(syncMapKey),
        };
      }

      return {
        ...competitor,
        mode,
        sport: _sport,
        competitionType: eventDetails?.competitionType,
        sync: syncMapped as SyncType,
      };
    });

    const _competitorsFromEventDetails = !!swapCompetitors ? [...competitorsFromEventDetails].reverse() : competitorsFromEventDetails;
    let [firstCompetitor, secondCompetitor]: (CompetitorType | undefined)[] = _competitorsFromEventDetails;

    return { firstCompetitor, secondCompetitor, hasCompetitors, hasSync };
  }, [eventDetails, mode, sport, swapCompetitors]);

  const _onClick =
    typeof onEventHeaderClick === "function"
      ? { onClick: _onEventHeaderClick }
      : {};

  if (!eventDetails) return null;
  return (
    <div
      className={`cui__event-header ${
        hasOnClickHandler ? "active:cui-bg-bg-layer-one cui-cursor-pointer" : ""
      } ${isOddsBoost ? "" : marginClasses} ${paddingClasses} ${className}`.trim()}
      {..._onClick}
      {...rest}
    >
      {eventDetails?.eventName ? (
        <>
          {/* <Details
            list={_eventName}
            className={cx(
              {
                "cui-mb-1": (hasCompetitors && !eventDetails?.description) || Boolean(isOddsBoost),
              },
              "!cui-p-0 cui-ml-0"
            )}
            noMinHeight
            noWrap
          /> */}
          <div className="">
            { isOddsBoost ? <p className="cui-p-1 cui-pb-2px cui-mr-xs cui-my-0 cui-mb-2px cui-bg-highlight-five cui-rounded-2px cui-inline-block">
              <Icon name={isFullBoost ? "brand_boost_logo" : "icon_ui_boost"} size={ isFullBoost ? "3xl" : "sm"} color={"fg-global-black"} />
            </p> : null}
            { eventDetails?.eventName ? <p className={`cui-text-fg-default cui-inline ${isOddsBoost ? "display-sm-bold" : "heading-sm-bold"}`}>{eventDetails?.eventName}</p> : null}
            { !(showTeamsInEventHeader || !hasOnClickHandler) && showChevron ? <Icon name="ui_chevron-right" size={14} color="fg-primary" className=" cui-translate-x-1 cui-translate-y-2px" /> : null } 
          </div>
          {eventDetails?.description ? (
            <span className="cui-block cui-mb-xs cui-text-fg-moderate body-xs">
              {eventDetails?.description}
            </span>
          ) : null}
        </>
      ) : null}
      {hasCompetitors && showTeamsInEventHeader ? (
        <EventHeaderCompetitors
          firstCompetitor={firstCompetitor}
          secondCompetitor={secondCompetitor}
          isLive={eventDetails?.isLive}
          showChevron={showChevron}
          hasSync={hasSync}
          hasEventDetails={!!eventDetails}
        />
      ) : null}
      {eventDetails ? (
        <EventDetails
          {...eventDetails}
          noPad
          noMinHeight
          textSize={!eventDetails?.isLive ? "body-xs-bold" : undefined}
        />
      ) : null}
    </div>
  );
};

const EventHeaderCompetitors = ({
  firstCompetitor,
  secondCompetitor,
  isLive,
  hasSync,
  showChevron,
  hasEventDetails
}: EventHeaderCompetitors) => {
  if (!firstCompetitor && !secondCompetitor && !hasSync) return null;
  return (
    <div className="cui-flex cui-gap-sm">
      {/* Competitors  */}
      <div className="cui-flex cui-flex-col cui-w-full cui-mb-xs">
        <Competitor {...firstCompetitor} force="shortname" textSize="heading-md" />
        <Competitor {...secondCompetitor} force="shortname" textSize="heading-md" />
      </div>
      {/* Chevron  */}
      {showChevron ? (
        <div className={`cui-flex cui-items-center cui-justify-center ${hasEventDetails ? "-cui-translate-y-1" : ""}`}>
          <Icon name="ui_chevron_right" />
        </div>
      ) : null}
    </div>
  );
};
